import { useAuth } from '../contexts/AuthContext'

/**
 * Hook for `<video>` / `<audio>` src URLs.
 *
 * Returns the given URL unchanged. Its only job is to subscribe the
 * consuming component to the auth context so that when the Supabase
 * session refreshes (every ~50 minutes), the parent re-renders, the
 * caller's `getAssetUrl(...)` call re-runs, and the now-fresh JWT
 * (read via `getCachedAuthToken()`) lands in the URL.
 *
 * The URL itself — including `?token=`, `?publication_token=`, `?v=` (cacheKey),
 * and `?w=` (width) — is built by `getAssetUrl()` at the call site. That is the
 * single URL builder for all assets (both `<img>` and `<video>` paths). Pass
 * `cacheKey` as the 4th arg of `getAssetUrl(projectId, path, 0, cacheKey)`
 * so cache-busting is honored.
 *
 * History: this hook previously appended `?token=...` and `?_=cacheKey` itself.
 * Because `getAssetUrl()` *also* appends `?token=`, the result was a malformed
 * URL like `…?token=A?token=A&_=cacheKey` that parsed as a single token value
 * with 5 dots, failed JWT decoding, and made `<video>` elements silently 401.
 * The fix is to centralize URL construction in `getAssetUrl()` and reduce this
 * hook to a re-render trigger on auth changes.
 */
export function useAuthenticatedMediaUrl(url: string | null, _cacheKey?: string | null): string | null {
  // Subscribe to auth context so the URL re-evaluates on token rotation.
  // The hook intentionally does not transform `url` — see the note above.
  useAuth()
  return url
}
